/**
 * main.tsx — Bootstraps the SPA.
 *
 *  1. Builds the router instance from the auto-generated route tree.
 *  2. Creates the QueryClient and threads it through `__root` via context.
 *  3. Mounts React to the #root element.
 *
 * The router is initialised with `defaultPreload: 'intent'` so links prefetch
 * their loaders on hover/focus (Phase B will deepen this with `loader` hooks).
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { routeTree } from './routes.generated.js';
import { makeQueryClient, type RouterContext } from './routes/__root.js';

const queryClient = makeQueryClient();

const router = createRouter({
  routeTree,
  context: { queryClient } satisfies RouterContext,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  // Manual hash-anchor scroll handled in __root.tsx (N-26); disable the
  // built-in scroll restoration behaviour to avoid the deprecation warning.
  scrollRestoration: false,
});

// NOTE: 2026-07-19 the deployed production bundle at
// https://coding-class-dev.terusmengajar.workers.dev is BROKEN with
// `TypeError: Cannot read properties of undefined (reading 'firstId')`.
// Root cause: mismatch between @tanstack/react-router@1.170.18 and
// @tanstack/router-core@1.171.15 — RouterCore constructor does NOT
// auto-call this.update() so router.stores never get created.
//
// Workaround attempts in this file (router.update({}) shim, exposing
// on globalThis, passing notFoundMode prop) all work in dev but FAIL
// in production because Vite minifier tree-shakes createRouterStores.
//
// Resolution deferred to next session — see
// tm-ai-workspace/reports/coding-class-portal/2026-07-19-deployment-blank-page-bug.md
// for the recommended fix (try pnpm overrides pinning router-core).
// This comment block can be removed once the production bundle renders.

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found.');
}

createRoot(rootElement).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
